import { Fragment, useEffect, useState } from 'react'
import {
  ApiError, addPenCount, penHistory,
  type PenHistory,
} from '../api'
import { antallFor, dagMnd, isoUke, nb } from './Varsel'
import { useT } from '../lang'

/* Every count this farm has, ring by ring, weeks running down.
 *
 * THIS IS THE ONLY PLACE THE FEATURE PAYS OFF IN FULL.
 *
 * One week of merd rows says which ring is worst today, which is useful and is
 * roughly what a good spreadsheet gives you. A YEAR of them says whether it is
 * the same ring every time — and "merd 3 is always the one at the mouth" is a
 * claim about geography that no single week can make and no site-level series
 * can make at all. A column that runs warm down its whole length is a mooring
 * decision, not a delousing decision.
 *
 * READ DOWN A COLUMN, NOT ACROSS A ROW. That is why the merder are columns and
 * the weeks are rows: the comparison worth making is one ring against its own
 * past, and rows are for the thing you scan.
 *
 * ONLY VOKSNE HUNNLUS GETS THE COLOUR LADDER, and that is the load-bearing
 * decision in this file.
 *
 * The limit in lakselusforskriften is written against adult females alone.
 * Bevegelige and fastsittende have NO threshold — `antallFor` returns null for
 * them by construction — so painting them green/amber/red would invent a rule
 * that does not exist, and would do it in the one direction that matters: a
 * reader who has learnt that red means "you must treat" would read it here and
 * be wrong.
 *
 * So those two use the app's existing answer to "a quantity with no threshold",
 * which is `.merke-kvart.maalt > i` in Dogn.tsx: proportional HEIGHT IN A FIXED
 * FRAME, one neutral hue, number still printed. Never an opacity ramp — Dogn
 * refuses that explicitly, because a reader counts symbols and cannot judge
 * area. The ceiling is the whole grid's maximum for that stage rather than each
 * row's, for WelfareScale's reason: same geometry every time you look, so two
 * cells of equal height mean the same thing.
 *
 * THREE LAYERS, AND THE OLDEST IS THE ONE THE CUSTOMER NEVER TYPED. Under the
 * farm's own weeks sit the register's, going back as far as it can answer —
 * which for a farm signing in today is years before they touched this product.
 * That inverts the usual problem: a data product is normally worthless until
 * you have fed it for a season, and this one opens on six years of the
 * customer's own history.
 *
 * A GAP IS DRAWN, NOT CLOSED UP. The upstream register answers in 26-week
 * windows per production cycle, so the coverage has real holes — nothing
 * between November 2022 and January 2024 at Ljøsøy. Two rows eighteen months
 * apart sitting flush against each other would read as two consecutive weeks,
 * which is the one thing a table must never do. Doegnstripe's rule: "hullet
 * mellom dem ER stoppen … fraværet gjør jobben."
 *
 * IT IS EDITED IN PLACE, LIKE THE SPREADSHEET IT REPLACES.
 *
 * The thing their Excel does best is that you click a cell and type. Every
 * correction we make somebody open a form for is a correction that costs more
 * than it did before, and a product that is better at everything except fixing
 * a typo is a product people keep a spreadsheet beside.
 *
 * So a cell is a cell: click, type, Enter or Tab, saved. Enter moves DOWN the
 * column, because a sheet is read one stage at a time. Escape restores. The
 * write is the ordinary `POST /pen-counts` — the same upsert the counting grid
 * uses, so a correction here and a correction there are the same act and cannot
 * disagree.
 *
 * ONLY THE FARM'S OWN MERD ROWS ARE EDITABLE. The register's weeks are somebody
 * else's record and we do not get to retype them; a farm figure spread across
 * the rings is not a per-ring measurement and typing into it would invent one.
 * Those cells are inert, and the cursor says so.
 *
 * THE SEAM IS DRAWN, NOT SMOOTHED. Every farm has years of site-level counts
 * and, at best, weeks of per-merd ones. Where a week has no rings, the farm's
 * own figure is painted across the whole row — the customer asked for that, and
 * a row of blanks under a week that was properly counted and properly reported
 * would be a worse lie than the average. But it is painted DASHED and stated in
 * the legend, because a week where every ring reads 0,31 and a week where every
 * ring happened to be 0,31 are different weeks. Smoothing the two together
 * would destroy the one thing this grid exists to show: when the per-merd record
 * actually starts.
 */

/** The band, from the model's own rule via `antallFor`. Same ladder as the map
 *  above and the forecast strip below, so a colour means one thing on this
 *  page. */
function band(v: number | null, grense: number | null): number {
  if (v === null) return 0
  return antallFor(v, grense) ?? 0
}

/** The three stages vedlegg 1 is written in.
 *
 *  `grense` is what separates them: only adult females have a limit, and that
 *  is a fact about the regulation rather than about our data. Everything the
 *  grid does differently for the other two follows from this one field. */
const STADIER = [
  { key: 'adult_female', label: 'Adult female', grense: true },
  { key: 'mobile', label: 'Mobile', grense: false },
  { key: 'sessile', label: 'Sessile', grense: false },
] as const

type Stadium = (typeof STADIER)[number]['key']

/** One cell, drawn two different ways for two different kinds of claim.
 *
 *  With a limit: the regulation's own three-band ladder, as a fill.
 *  Without one: a bar whose HEIGHT is the share of the grid's maximum, in one
 *  neutral hue, with the number still printed on top. Dogn.tsx's
 *  `.merke-kvart.maalt > i` exactly — proportion in a fixed frame, shared
 *  baseline, shared ceiling — because that is the only shape in this app a
 *  reader can read a quantity back off without a scale to compare it to.
 */
function Rute({ v, grense, tak, avledet, utelt, tom, anlegg, title,
                rediger, verdi, busy, onStart, onChange, onCommit, onCancel }: {
  v: number | null
  grense: number | null
  /** The grid-wide maximum for this stage, or null when the stage has a limit
   *  and is therefore drawn against the ladder instead. */
  tak: number | null
  avledet?: boolean
  utelt?: boolean
  /** The ring held no fish that week. Different from uncounted, and it is the
   *  thing that explains a column going quiet without anybody having failed to
   *  do their job. */
  tom?: boolean
  anlegg?: boolean
  title?: string
  /** Editable, and being edited. Absent means inert — the register's weeks and
   *  a farm figure spread across the rings are not per-ring measurements, and
   *  typing into either would invent one. */
  rediger?: boolean
  verdi?: string
  busy?: boolean
  onStart?: () => void
  onChange?: (v: string) => void
  onCommit?: (goDown: boolean) => void
  onCancel?: () => void
}) {
  const cls = [
    'num',
    anlegg ? 'anlegg' : '',
    tak === null ? `b${band(v, grense)}` : 'rampe',
    avledet ? 'avledet' : '',
    // A ring that could not be counted is a reported fact and not a hole:
    // weather, a delousing, an empty ring.
    utelt ? 'utelt' : '',
    tom ? 'tomrute' : '',
    onStart ? 'redigerbar' : '',
    busy ? 'lagrer' : '',
  ].filter(Boolean).join(' ')
  return (
    <td className={cls} title={title || undefined}
      onClick={onStart && !rediger ? onStart : undefined}>
      {/* The bar is a child rather than a background, so `.avledet`'s stripes
          and `.utelt`'s dashed frame keep working on the same cell. */}
      {tak !== null && v !== null && !utelt && !rediger && (
        <i style={{ height: `${Math.round(Math.min(1, v / tak) * 100)}%` }} />
      )}
      {rediger ? (
        <input autoFocus inputMode="decimal" value={verdi}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={() => onCommit?.(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onCommit?.(true) }
            else if (e.key === 'Tab') onCommit?.(false)
            else if (e.key === 'Escape') { e.preventDefault(); onCancel?.() }
          }} />
      ) : (
        <span>{tom ? '·' : utelt ? '×' : v !== null ? nb(v) : '–'}</span>
      )}
    </td>
  )
}

const UKE_MS = 604800000

/** Weeks between two dated rows. Anything over one is a hole in the record. */
function ukerMellom(nyere: string, eldre: string): number {
  return Math.round(
    (Date.parse(nyere) - Date.parse(eldre)) / UKE_MS)
}

/** Roughly "aug 2024", for naming the two ends of a gap. */
function maaned(iso: string): string {
  return new Date(iso + 'T00:00:00Z').toLocaleDateString('nb-NO', {
    month: 'short', year: 'numeric', timeZone: 'UTC',
  })
}

export function Merdhistorikk({ siteId, grenseFor, canWrite, onChanged }: {
  siteId: string
  /** Editing is the same write the counting grid makes, so it needs the same
   *  permission. Without it every cell is inert and the grid is a record. */
  canWrite?: boolean
  /** A correction here moves the farm's own figure for that day, so the page
   *  above has to rebuild. */
  onChanged?: () => void
  /** The limit in force for the week a given date falls in — 0,50 normally,
   *  0,20 in weeks 16–21, 0,10 there in a red production area. Passed as a
   *  function and read per row, never once: a grid that coloured a whole year
   *  against August's limit would be wrong every April. */
  grenseFor: (iso: string) => number | null
}) {
  const t = useT()
  const [data, setData] = useState<PenHistory | null>(null)
  const [err, setErr] = useState<string | null>(null)
  /* Voksne hunnlus by default, because it is the one with a limit, the one
     that decides a treatment and the one sent to Mattilsynet. The other two
     are what a reader goes looking for. */
  const [stadium, setStadium] = useState<Stadium>('adult_female')
  /** The cell being typed into, as `${counted_at}|${pen_id}`. */
  const [redigerer, setRedigerer] = useState<string | null>(null)
  const [utkast, setUtkast] = useState('')
  const [lagrer, setLagrer] = useState<string | null>(null)

  useEffect(() => {
    let dead = false
    void penHistory(siteId).then((r) => { if (!dead) setData(r) })
      .catch((e) => {
        if (!dead) setErr(e instanceof ApiError ? (e.detail ?? e.code)
          : t('Could not reach the receiver.'))
      })
    return () => { dead = true }
  }, [siteId])

  /* The same upsert the counting grid uses, and deliberately not a new route.
     A correction made here and a correction made there are the same act; two
     ways to write one number is how the two come to disagree. */
  const lagre = async (day: string, penId: string, raw: string) => {
    const key = `${day}|${penId}`
    setRedigerer(null)
    const v = raw.trim().replace(',', '.')
    if (v === '') return
    const n = Number(v)
    if (!Number.isFinite(n) || n < 0) { setErr(t('That is not a number.')); return }
    setLagrer(key); setErr(null)
    try {
      const r = await addPenCount(siteId, {
        pen_id: penId, counted_at: day, [stadium]: n,
      } as Parameters<typeof addPenCount>[1])
      // Patched in place rather than refetched: a grid that reloads and jumps
      // back to the top after every cell is a grid nobody types a column into.
      setData((d) => (d ? {
        ...d,
        days: d.days.map((x) => (x.counted_at !== day ? x : {
          ...x,
          cells: Object.fromEntries(
            r.items.filter((p) => p.pen_count_id).map((p) => [p.pen_id, {
              adult_female: p.adult_female, mobile: p.mobile,
              sessile: p.sessile, n_fish: p.n_fish,
              not_counted_reason: p.not_counted_reason, basis: p.basis ?? '',
            }])),
          adult_female: r.site?.adult_female ?? x.adult_female,
          mobile: r.site?.mobile ?? x.mobile,
          sessile: r.site?.sessile ?? x.sessile,
          basis: r.site?.basis ?? 'pens',
        })),
      } : d))
      onChanged?.()
    } catch (e) {
      setErr(e instanceof ApiError ? (e.detail ?? e.code)
        : t('Could not reach the receiver.'))
    } finally { setLagrer(null) }
  }

  /* Enter moves DOWN the column, the same rule the counting grid follows: a
     sheet is read one stage at a time, and tab-across makes the reader zigzag
     between two documents. Tab still goes across for anyone who reads that
     way. */
  const neste = (day: string, penId: string) => {
    const rows = data?.days ?? []
    const i = rows.findIndex((d) => d.counted_at === day)
    for (let j = i + 1; j < rows.length; j++) {
      if (rows[j]!.basis !== 'register'
          && Object.keys(rows[j]!.cells).length > 0) {
        return `${rows[j]!.counted_at}|${penId}`
      }
    }
    return null
  }

  if (err) return <div className="notice bad">{err}</div>
  if (!data) return null
  if (data.days.length === 0) return null

  // Nothing to spread across. A farm with no rings registered gets the week
  // table below and no grid, which is the whole product it had before.
  if (data.pens.length === 0) return null

  const noenMerder = data.days.some((d) => Object.keys(d.cells).length > 0)
  const valgt = STADIER.find((x) => x.key === stadium)!

  /* THE CEILING IS THE WHOLE GRID'S, not each row's.
   *
   * WelfareScale's rule: "same geometry every time you look, so the eye learns
   * where 135 is and reads the bar against it without reading a number." Scaled
   * per row, two cells of equal height would mean different quantities and the
   * column — the thing this grid exists to be read down — would say nothing.
   *
   * Only computed for the stages that have no limit. Adult females are drawn
   * against the regulation's ladder and need no ceiling of ours. */
  const tak = valgt.grense ? null : Math.max(
    0.0001,
    ...data.days.flatMap((d) => [
      d[stadium] ?? 0,
      ...Object.values(d.cells).map((c) => c[stadium] ?? 0),
    ]))

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row" style={{ justifyContent: 'space-between',
                                    alignItems: 'baseline', gap: 12,
                                    flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>{t('Every week, by pen')}</h3>
        {/* `.seg.sm.stille` and not `.seg`: this page already spends --signal
            on the rings, and styles.css:457 argues that a second filled
            control on the same screen gives three things a voice where one
            should have it. A quiet underline is enough to say where you are. */}
        <div className="seg sm stille" role="tablist"
          aria-label={t('Stage')}>
          {STADIER.map((x) => (
            <button key={x.key} type="button" role="tab"
              aria-selected={stadium === x.key}
              onClick={() => setStadium(x.key)}>{t(x.label)}</button>
          ))}
        </div>
      </div>

      <div className="tabellrull">
        <table className="tbl merdrutenett">
          <thead>
            <tr>
              <th>{t('Week')}</th>
              <th className="num anlegg">{t('The site')}</th>
              {data.pens.map((p) => (
                <th key={p.pen_id} className="num"
                  title={p.retired ? t('retired') : (p.name ?? '')}>
                  {p.pen_no}
                  {p.retired && <span className="faint"> ·</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.days.map((d, i) => {
              const grense = grenseFor(d.counted_at)
              /* The hole between this row and the one above it. Rows come
                 newest first, so the gap is measured against the PREVIOUS
                 element. Two weeks apart is an ordinary fortnightly cadence at
                 4-10 °C; more than that and the record actually stops. */
              const forrige = data.days[i - 1]
              const hull = forrige
                && ukerMellom(forrige.counted_at, d.counted_at) > 3
                ? forrige : null
              /* No rings that day: the farm's figure is what there is, and it
                 goes in every column. Drawn dashed and captioned below, never
                 as eight measurements. */
              // Both a site-level week and a register week have no rings
              // under them, and both are spread across the columns for the
              // same reason: the number is true of the FARM. Which of the two
              // it is gets said in the week column, not in the fill.
              const felles = Object.keys(d.cells).length === 0
                ? (d[stadium] ?? null) : null
              return (
                <Fragment key={d.counted_at}>
                {hull && (
                  /* Drawn, not closed up. Two rows eighteen months apart
                     sitting flush would read as two consecutive weeks. */
                  <tr className="hullrad">
                    <td colSpan={2 + data.pens.length}>
                      {t('no data')} {maaned(d.counted_at)}{' → '}
                      {maaned(hull.counted_at)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td data-l={t('Week')}>
                    <strong>{t('Week')} {isoUke(d.counted_at)}</strong>
                    <span className="faint"> {dagMnd(d.counted_at)}</span>
                    {/* Where the figure came from, said in the one column that
                        has room for a word. The stripes already mean "the
                        locality's figure, spread across the rings" and mean the
                        same thing for both — so the source goes here rather
                        than taking a second meaning from the stripe. */}
                    {d.basis === 'register' && (
                      <span className="kilde">{t('register')}</span>
                    )}
                  </td>
                  <Rute v={d[stadium] ?? null} grense={grense} tak={tak}
                    anlegg />
                  {data.pens.map((p) => {
                    const c = d.cells[p.pen_id]
                    const v = felles !== null
                      ? felles : (c?.[stadium] ?? null)
                    const tom = (d.empty_pens ?? []).includes(p.pen_id)
                    const key = `${d.counted_at}|${p.pen_id}`
                    /* Editable only where the number would be this ring's own.
                       The register's weeks belong to somebody else, and a farm
                       figure spread across the rings is not a measurement of
                       any of them — typing into either would invent one. */
                    const kanRedigere = !!canWrite && d.basis !== 'register'
                      && felles === null && !tom
                    return (
                      <Rute key={p.pen_id} v={v} grense={grense} tak={tak}
                        avledet={felles !== null}
                        utelt={!!c?.not_counted_reason} tom={tom}
                        busy={lagrer === key}
                        rediger={redigerer === key}
                        verdi={utkast}
                        onStart={kanRedigere ? () => {
                          setRedigerer(key)
                          setUtkast(v === null ? '' : nb(v))
                        } : undefined}
                        onChange={setUtkast}
                        onCancel={() => setRedigerer(null)}
                        onCommit={(goDown) => {
                          const raw = utkast
                          const n = goDown
                            ? neste(d.counted_at, p.pen_id) : null
                          void lagre(d.counted_at, p.pen_id, raw).then(() => {
                            if (n) { setRedigerer(n); setUtkast('') }
                          })
                        }}
                        title={tom ? t('No fish in this pen that week.')
                          : c?.not_counted_reason
                            ? t(`skipped:${c.not_counted_reason}`)
                            : felles !== null
                              ? t("The farm's figure, not this pen's.") : ''} />
                    )
                  })}
                </tr>
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="kartlegende">
        {valgt.grense ? (
          ([[1, t('Under half the limit')], [2, t('Half to the limit')],
            [3, t('At or over the limit')]] as const).map(([n, label]) => (
            <span key={n} className={`legendbit b${n}`}>{label}</span>
          ))
        ) : (
          /* NO LADDER, AND THE LEGEND SAYS SO IN WORDS. A reader who has learnt
             what red means on the other tab must not carry it here, where there
             is no limit for it to mean anything against. */
          <span className="legendbit rampe">
            {t('No limit for this stage — height is a share of the highest figure in the table')}
            {' ('}{nb(tak ?? 0)}{')'}
          </span>
        )}
        <span className="legendbit stiplet">
          {t("The farm's figure, spread across the pens")}
        </span>
      </div>

      {/* Where the record actually begins. Worth saying once, because it is the
          honest answer to "why does the top half look different" and because
          the date is a real milestone for this farm. */}
      {!noenMerder && (
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          {t('No week has been counted per pen yet, so every row shows the farm figure.')}
        </p>
      )}
    </div>
  )
}
